/**
 * 화면 표시 관련 범용 유틸리티 함수들
 * 도메인에 종속되지 않은 재사용 가능한 표시 로직들을 모아둠
 */

/**
 * 총 페이지 수 계산
 * @param {number} totalCount - 전체 아이템 개수
 * @param {number} itemsPerPage - 페이지당 아이템 개수
 * @returns {number} 총 페이지 수 (최소 1)
 */
export function calculatePageCount(totalCount, itemsPerPage) {
  if (totalCount === 0 || itemsPerPage === 0) return 1;
  const hasRest = totalCount % itemsPerPage !== 0;
  return Math.floor(totalCount / itemsPerPage) + (hasRest ? 1 : 0);
}

/**
 * 시간 범위를 포맷팅하여 문자열로 반환 (한국 locale 기본 사용)
 * @param {string|Date} startTime - 시작 시간 (ISO 문자열 또는 Date 객체)
 * @param {string|Date|null|undefined} endTime - 종료 시간 (ISO 문자열, Date 객체, null, undefined)
 * @param {Object} options - 옵션 객체
 * @param {string} options.locale - 로케일 (기본값: 'ko-KR')
 * @param {string} options.inProgressText - 진행 중일 때 표시할 텍스트 (기본값: '진행 중')
 * @param {string} options.durationPrefix - 소요 시간 앞에 붙일 텍스트 (기본값: '소요: ')
 * @returns {string} 포맷팅된 시간 범위 문자열
 * 
 * @example
 * formatTimeRange('2024-11-20T02:27:19.603+00:00', null);
 * // "2024. 11. 20. 오전 11:27:19 ~ 진행 중"
 * 
 * formatTimeRange('2024-11-20T02:27:19.603+00:00', '2024-11-20T02:30:19.603+00:00');
 * // "2024. 11. 20. 오전 11:27:19 ~ 2024. 11. 20. 오전 11:30:19 (소요: 3분)"
 */
export function formatTimeRange(startTime, endTime, options = {}) {
  if (!startTime) return '';
  
  const start = new Date(startTime);
  if (isNaN(start.getTime())) return '';
  
  const locale = options.locale || 'ko-KR';
  const startFormatted = start.toLocaleString(locale);
  const inProgressText = options.inProgressText || '진행 중';
  
  if (!endTime) {
    return `${startFormatted} ~ ${inProgressText}`;
  }
  
  const end = new Date(endTime);
  if (isNaN(end.getTime())) return `${startFormatted} ~ ${inProgressText}`;
  
  const endFormatted = end.toLocaleString(locale);
  const duration = end - start;
  const durationMinutes = Math.floor(duration / 1000 / 60);
  const durationPrefix = options.durationPrefix || '소요: ';
  
  if (durationMinutes < 60) {
    return `${startFormatted} ~ ${endFormatted} (${durationPrefix}${durationMinutes}분)`;
  } else {
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    return `${startFormatted} ~ ${endFormatted} (${durationPrefix}${hours}시간 ${minutes}분)`;
  }
}

/**
 * 문자열을 앞부분과 뒷부분을 보존하면서 중간만 생략하는 형식으로 변환하는 유틸리티 함수
 * 
 * 두 가지 타입을 지원합니다:
 * 1. 'length' 타입: 앞부분과 뒷부분의 글자 수를 고정하여 표시 (파일명 등 앞뒤가 중요한 경우)
 * 2. 'maxLength' 타입: 최대 글자 수만 제한하여 표시 (CSS ellipsis와 함께 사용)
 * 3. 'css' 타입: 원문 그대로 반환 — 컨테이너 width + text-truncate가 말줄임 처리
 * 4. 'fitWidth' 타입: RailListEllipsisTooltip 등에서 컨테이너 width 기반 중간 ellipsis (ellipsisStringForWidth)
 * @param {string} text - 원본 문자열 (null, undefined, 빈 문자열인 경우 빈 문자열 반환)
 * @param {Object} [options={}] - 옵션 객체
 * @param {('length'|'maxLength'|'css')} [options.type='length'] - 생략 방식 타입
 *   - 'length': 앞부분과 뒷부분의 글자 수를 고정하여 표시 (기본값)
 *   - 'maxLength': 최대 글자 수만 제한하여 표시
 *   - 'css': 원문 반환 (부모 width에 따른 CSS ellipsis 사용)
 * @param {number} [options.frontLength=5] - 앞부분에 표시할 글자 수 (type이 'length'일 때만 사용)
 * @param {number} [options.backLength=11] - 뒷부분에 표시할 글자 수 (type이 'length'일 때만 사용)
 * @param {number} [options.maxLength] - 최대 표시할 글자 수 (type이 'maxLength'일 때 필수)
 * @returns {string} 포맷된 문자열. 원본이 짧으면 그대로 반환, 길면 생략 처리된 문자열 반환
 */
export function ellipsisString(text, options = {}) {
  if (!text) return '';
  
  const type = options.type || 'length';

  if (type === 'css') {
    return text;
  }
  
  if (type === 'maxLength') {
    // 최대 글자 수 제한 방식 (CSS ellipsis와 함께 사용)
    const maxLength = options.maxLength;
    if (!maxLength || text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength) + '...';
  } else {
    // 고정 글자 수 방식 (앞뒤 글자 수 지정)
    const frontLength = options.frontLength !== undefined ? options.frontLength : 5;
    const backLength = options.backLength !== undefined ? options.backLength : 11;
    
    // 전체 길이가 짧으면 그대로 반환
    const minLength = frontLength + backLength + 3; // 3은 "..." 길이
    if (text.length <= minLength) {
      return text;
    }
    
    // 앞부분과 뒷부분 추출
    const frontPart = text.substring(0, frontLength);
    const backPart = text.substring(text.length - backLength);
    
    return `${frontPart}...${backPart}`;
  }
}

const MIDDLE_ELLIPSIS = '...';
const WIDTH_FIT_SAFETY_PX = 1;

function fitsWidth(measureWidth, text, maxWidthPx) {
  return measureWidth(text) <= maxWidthPx - WIDTH_FIT_SAFETY_PX;
}

/**
 * 앞·뒤 글자 수로 중간 생략 문자열 생성
 * @param {string} text
 * @param {number} frontLength
 * @param {number} backLength
 */
export function buildMiddleEllipsis(text, frontLength, backLength) {
  if (!text) return '';
  const front = Math.max(0, Math.min(frontLength, text.length));
  const back = Math.max(0, Math.min(backLength, text.length - front));
  if (front + back >= text.length) {
    return text;
  }
  return `${text.substring(0, front)}${MIDDLE_ELLIPSIS}${text.substring(text.length - back)}`;
}

/**
 * 컨테이너 width에 맞춰 앞·뒤를 우선 보존하는 중간 ellipsis
 * @param {string} text
 * @param {number} maxWidthPx
 * @param {(value: string) => number} measureWidth
 * @param {Object} [options]
 * @param {number} [options.frontLength=5] - width 미측정 시 fallback 앞 글자 수
 * @param {number} [options.backLength=11] - width 미측정 시 fallback 뒤 글자 수
 * @param {number} [options.minFrontLength=1]
 * @param {number} [options.minBackLength=4]
 */
export function ellipsisStringForWidth(text, maxWidthPx, measureWidth, options = {}) {
  if (!text) return '';

  const fallbackFront = options.frontLength !== undefined ? options.frontLength : 5;
  const fallbackBack = options.backLength !== undefined ? options.backLength : 11;
  const minFront = options.minFrontLength !== undefined ? options.minFrontLength : 1;
  const minBack = options.minBackLength !== undefined ? options.minBackLength : 4;

  if (!maxWidthPx || maxWidthPx <= 0 || typeof measureWidth !== 'function') {
    return ellipsisString(text, {
      type: 'length',
      frontLength: fallbackFront,
      backLength: fallbackBack
    });
  }

  if (fitsWidth(measureWidth, text, maxWidthPx)) {
    return text;
  }

  const minTotal = minFront + minBack;
  const maxTotal = text.length - 1;

  for (let total = maxTotal; total >= minTotal; total -= 1) {
    const maxBackForTotal = Math.min(text.length - minFront - 1, total - minFront);
    for (let back = maxBackForTotal; back >= minBack; back -= 1) {
      const front = total - back;
      if (front < minFront || front + back >= text.length) {
        continue;
      }
      const candidate = buildMiddleEllipsis(text, front, back);
      if (fitsWidth(measureWidth, candidate, maxWidthPx)) {
        return candidate;
      }
    }
  }

  return buildMiddleEllipsis(text, minFront, minBack);
}

/**
 * DOM 요소 font 기준 텍스트 width 측정 (canvas measureText)
 * @param {HTMLElement} referenceElement
 */
export function createTextWidthMeasurer(referenceElement) {
  if (typeof document === 'undefined' || !referenceElement) {
    return text => String(text || '').length * 7;
  }

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  let cachedFont = '';

  const syncFont = () => {
    const style = window.getComputedStyle(referenceElement);
    const nextFont =
      style.font ||
      `${style.fontStyle} ${style.fontVariant} ${style.fontWeight} ${style.fontSize} / ${style.lineHeight} ${style.fontFamily}`;
    if (nextFont !== cachedFont && ctx) {
      cachedFont = nextFont;
      ctx.font = nextFont;
    }
  };

  return text => {
    if (!text) return 0;
    syncFont();
    return ctx ? ctx.measureText(String(text)).width : String(text).length * 7;
  };
}
